import { ArrowUpRight, ArrowDownLeft, CheckCircle, Circle } from "lucide-react";
import useTranslations from "@/shared/lang/useTranslations";

export type QuoterMode = "send" | "receive";

export type ExchangeRateDisplay = {
  rate: number;
  currencyFrom: string;
  currencyTo: string;
};

type QuoterStep1Props = {
  mode: QuoterMode;
  onModeChange: (mode: QuoterMode) => void;
  exchangeRate?: ExchangeRateDisplay | null;
  hideExchangeRateCard?: boolean;
};

const cardBase =
  "step-card group relative flex items-center p-6 border-2 cursor-pointer rounded-xl transition-all hover:shadow-md";
const cardActive =
  "border-green-500 bg-green-500/5 dark:bg-green-500/10";
const cardInactive =
  "border-gray-200 dark:border-zinc-800 hover:border-green-500/50";

function formatRate(value: number): string {
  return value.toLocaleString("es-CO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
}

export const QuoterStep1 = ({
  mode,
  onModeChange,
  exchangeRate = null,
  hideExchangeRateCard = false,
}: QuoterStep1Props) => {
  const { t } = useTranslations("quoter");
  return (
  <div className="lg:col-span-5 flex flex-col gap-6">
    <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400">
      {t("step1_title")}
    </h2>
    <div className="grid grid-cols-1 gap-4">
      <label
        className={`${cardBase} ${mode === "send" ? cardActive : cardInactive}`}
      >
        <input
          type="radio"
          name="mode"
          value="send"
          checked={mode === "send"}
          onChange={() => onModeChange("send")}
          className="sr-only"
        />
        <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-green-500 mr-5 shrink-0">
          <ArrowUpRight className="size-7" />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className={`font-bold text-lg ${
              mode === "send"
                ? "text-gray-900 dark:text-white"
                : "text-gray-700 dark:text-zinc-200"
            }`}
          >
            {t("step1_send_title")}
          </h3>
          <p className="text-sm text-gray-500 dark:text-zinc-400">
            {t("step1_send_description")}
          </p>
        </div>
        {mode === "send" ? (
          <CheckCircle className="size-6 text-green-500 shrink-0" />
        ) : (
          <Circle className="size-6 text-transparent group-hover:text-green-500/20 shrink-0" />
        )}
      </label>

      <label
        className={`${cardBase} ${mode === "receive" ? cardActive : cardInactive}`}
      >
        <input
          type="radio"
          name="mode"
          value="receive"
          checked={mode === "receive"}
          onChange={() => onModeChange("receive")}
          className="sr-only"
        />
        <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-zinc-800 flex items-center justify-center text-gray-500 dark:text-zinc-400 group-hover:bg-emerald-50 dark:group-hover:bg-emerald-900/10 group-hover:text-green-500 transition-colors mr-5 shrink-0">
          <ArrowDownLeft className="size-7" />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className={`font-bold text-lg ${
              mode === "receive"
                ? "text-gray-900 dark:text-white"
                : "text-gray-700 dark:text-zinc-200"
            }`}
          >
            {t("step1_receive_title")}
          </h3>
          <p className="text-sm text-gray-500 dark:text-zinc-400">
            {t("step1_receive_description")}
          </p>
        </div>
        {mode === "receive" ? (
          <CheckCircle className="size-6 text-green-500 shrink-0" />
        ) : (
          <Circle className="size-6 text-transparent group-hover:text-green-500/20 shrink-0" />
        )}
      </label>
    </div>

    {!hideExchangeRateCard && (
      <div className="mt-4 p-5 bg-gray-50 dark:bg-zinc-800/50 rounded-xl border border-gray-200 dark:border-zinc-800">
        <div className="flex items-center gap-2 text-green-500 font-medium mb-4">
          <span className="text-sm">{t("step1_rate_label")}</span>
        </div>
        {exchangeRate ? (
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500 dark:text-zinc-400 uppercase font-bold tracking-tight">
                1 {exchangeRate.currencyFrom} {t("step1_equiv_to")}
              </span>
              <span className="text-lg font-mono font-bold">
                {formatRate(exchangeRate.rate)} {exchangeRate.currencyTo}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500 dark:text-zinc-400 uppercase font-bold tracking-tight">
                1 {exchangeRate.currencyTo} {t("step1_equiv_to")}
              </span>
              <span className="text-lg font-mono font-bold">
                {formatRate(exchangeRate.rate !== 0 ? 1 / exchangeRate.rate : 0)}{" "}
                {exchangeRate.currencyFrom}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500 dark:text-zinc-400 uppercase font-bold tracking-tight">
              {t("step1_quote_to_see_rate")}
            </span>
            <span className="text-lg font-mono font-bold text-gray-400 dark:text-zinc-500">
              —
            </span>
          </div>
        )}
      </div>
    )}
  </div>
  );
};
